import React, { useRef, useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { motion } from "framer-motion";
import "./About.css";
import Globe from "react-globe.gl";

// Import local team member photos
import ajayPhoto from "../assets/images/ajay.jpg";
import sandeepthiPhoto from "../assets/images/sandeepthi.jpg";
import rayyanPhoto from "../assets/images/rayyan.jpg";
import kushalPhoto from "../assets/images/kushal.jpg";
import srinivasaraoPhoto from "../assets/images/srinivasarao.jpg";
import bedPhoto from "../assets/images/bed.png";

const About = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const globeRef = useRef(null);

  const team = [
    {
      name: "Ajay Polavarapu",
      role: "Director - Operations",
      linkedin: "https://linkedin.com/in/ajay-polavarapu-04a293255",
      img: ajayPhoto,
    },
    {
      name: "Sandeepthi Chitturi",
      role: "Research Lead",
      linkedin: "https://linkedin.com/in/sandeepthi-chitturi",
      img: sandeepthiPhoto,
    },
    {
      name: "Rayyan",
      role: "Tech Lead - Hardware",
      linkedin: "#",
      img: rayyanPhoto,
    },
    {
      name: "Kushal Thandra",
      role: "Tech Lead - Software",
      linkedin: "https://linkedin.com/in/kushal-thandra-ab2465189",
      img: kushalPhoto,
    },
    {
      name: "Dr. Srinivasarao Gummadi",
      role: "Director – Advisory",
      linkedin: "https://linkedin.com/in/dr-srinivasa-rao-gummadi-542a0771",
      img: srinivasaraoPhoto,
    },
  ];

  const handleWatchStory = () => {
    setIsVideoOpen(true);
  };

  const handleCompanyProfile = () => {
    window.open(
      "https://www.linkedin.com/company/pranayuv-technologies/posts/?feedView=all",
      "_blank"
    );
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? 1 : 0));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? 1 : 0));
  };

  useEffect(() => {
    AOS.init({
      duration: 1200,
      once: true,
      easing: "ease-out-cubic",
    });
  }, []);

  // Coordinates for Vijayawada and Visakhapatnam
  const marker = [
    {
      lat: 16.482111,
      lng: 80.691361,
      label: "Vijayawada, Andhra Pradesh - Innovation Hub",
      color: "#ff6b6b",
      size: 25,
    },
    {
      lat: 17.6868,
      lng: 83.2185,
      label: "AMTZ Campus, Visakhapatnam, Andhra Pradesh - Research Center",
      color: "#4ecdc4",
      size: 25,
    },
  ];

  // Globe animation
  useEffect(() => {
    let animationFrameId;

    const animate = () => {
      if (globeRef.current) {
        const currentLng =
          (globeRef.current.pointOfView()?.lng || (80.691361 + 83.2185) / 2) +
          0.15;
        globeRef.current.pointOfView(
          {
            lat: (16.482111 + 17.6868) / 2,
            lng: currentLng,
            altitude: 2.5,
          },
          15
        );
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    const timeoutId = setTimeout(() => {
      if (globeRef.current) {
        globeRef.current.pointOfView(
          {
            lat: (16.482111 + 17.6868) / 2,
            lng: (80.691361 + 83.2185) / 2,
            altitude: 2.5,
          },
          0
        );
        animate();
      }
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <div className="about-page p-0">
      <div className="background-design">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      {/* Hero Section */}
      <motion.section className="hero-section2">
        <div className="container-fluid px-4">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <motion.h1
                className="display-3 fw-bold mb-4"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                Our Journey of Innovation
              </motion.h1>
              <motion.p
                className="lead mb-4"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                Transforming healthcare through cutting-edge technology and
                compassionate design. From student vision to industry innovation
                - discover how we're reshaping the future of medical care.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                <button
                  className="btn btn-gradient1 btn-lg me-3"
                  onClick={handleWatchStory}
                >
                  <i className="bi bi-play-fill me-2"></i>
                  Watch Our Story
                </button>
                <button
                  className="btn btn-outline-gradient1 btn-lg"
                  onClick={handleCompanyProfile}
                >
                  <i className="bi bi-download me-2"></i>
                  Company Profile
                </button>
              </motion.div>
            </div>
            <div className="col-lg-6">
              <motion.img
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80"
                alt="Innovation Team"
                className="img-fluid hero-image w-100"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                style={{ maxHeight: "500px", objectFit: "cover" }}
              />
            </div>
          </div>
        </div>
      </motion.section>

      {/* Main Content Section - All in one continuous flow */}
      <section className="py-5 px-3">
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {/* Technology Trends Section */}
              <div className="mb-5">
                <div className="p-5">
                  <div className="row align-items-center">
                    <div className="col-lg-6 mb-4 mb-lg-0">
                      <h2 className="section-title text-start mb-4">
                        Technology Excellence
                      </h2>
                      <div className="row g-3">
                        {[bedPhoto].map((src, index) => (
                          <motion.div
                            key={index}
                            className="col-12"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.1, duration: 0.6 }}
                            viewport={{ once: true }}
                          >
                            <img
                              src={src}
                              alt="Hospital Bed Technology"
                              className="img-fluid tech-image w-100"
                              style={{ height: "300px", objectFit: "cover" }}
                            />
                          </motion.div>
                        ))}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="ps-lg-4">
                        <motion.div
                          initial={{ opacity: 0, x: 30 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.8 }}
                          viewport={{ once: true }}
                        >
                          <div className="d-flex align-items-center mb-4">
                            <div className="bg-gradient rounded-circle p-3 me-3">
                              <i className="bi bi-lightbulb text-white fs-4"></i>
                            </div>
                            <h3 className="card1-title h2 mb-0">Our Mission</h3>
                          </div>
                          <p className="lead text-muted mb-4">
                            At Pranayuv, we envision a world where healthcare is
                            smarter, more compassionate, and accessible to all.
                            We're on a mission to build innovative wellness
                            products that are both technologically advanced and
                            affordable.
                          </p>

                          <div className="d-flex align-items-center mb-4 mt-5">
                            <div className="bg-gradient rounded-circle p-3 me-3">
                              <i className="bi bi-eye text-white fs-4"></i>
                            </div>
                            <h3 className="card1-title h2 mb-0">Our Vision</h3>
                          </div>
                          <p className="lead text-muted">
                            To lead in healthcare innovation, transforming lives
                            through smart medical technology. We envision a
                            future where everyone has access to personalized,
                            efficient, and compassionate care that enhances
                            well-being.
                          </p>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Journey Section */}
              <div className="mb-5">
                <div className="pt-3 px-5 pb-4">
                  <h2 className="section-title mb-2">Our Innovation Journey</h2>
                  <div className="row">
                    <div className="col-lg-4 mb-4">
                      <div className="text-center">
                        <div className="bg-gradient rounded-circle p-4 d-inline-flex mb-3">
                          <i className="bi bi-rocket-takeoff text-white fs-2"></i>
                        </div>
                        <h4 className="card1-title">The Beginning</h4>
                        <p className="text-muted">
                          Founded in April 2025 by P.S. Rama Krishna, starting
                          as a student-led initiative in the labs of Siddhartha
                          Academy.
                        </p>
                      </div>
                    </div>
                    <div className="col-lg-4 mb-4">
                      <div className="text-center">
                        <div className="bg-gradient rounded-circle p-4 d-inline-flex mb-3">
                          <i className="bi bi-gear-wide-connected text-white fs-2"></i>
                        </div>
                        <h4 className="card1-title">Innovation</h4>
                        <p className="text-muted">
                          Developed the Ayuv Hybrid Convertible Bed - a smart
                          bed-to-wheelchair system with IoT monitoring and
                          automated sanitation.
                        </p>
                      </div>
                    </div>
                    <div className="col-lg-4 mb-4">
                      <div className="text-center">
                        <div className="bg-gradient rounded-circle p-4 d-inline-flex mb-3">
                          <i className="bi bi-graph-up-arrow text-white fs-2"></i>
                        </div>
                        <h4 className="card1-title">Growth</h4>
                        <p className="text-muted">
                          Launched AyuvDrop Smart Water Bottle and expanded our
                          mission to create multiple healthcare solutions.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <p className="lead text-muted">
                      From a bold student idea to a purpose-driven company
                      creating real-world healthcare solutions - our journey
                      continues with each innovation making healthcare more
                      accessible and intelligent.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mission/Vision Carousel */}
              <div className="mb-5">
                <div className="p-5">
                  <div
                    id="missionVisionCarousel"
                    className="carousel slide"
                    data-bs-ride="carousel"
                  >
                    <div className="carousel-inner">
                      <motion.div
                        className={`carousel-item ${
                          currentSlide === 0 ? "active" : ""
                        }`}
                        initial={{ opacity: 0, x: 100 }}
                        animate={{
                          opacity: currentSlide === 0 ? 1 : 0,
                          x: currentSlide === 0 ? 0 : -100,
                        }}
                        transition={{ duration: 0.6 }}
                      >
                        <div className="text-center">
                          <motion.div
                            className="bg-gradient rounded-circle p-4 d-inline-flex mb-4"
                            initial={{ scale: 0 }}
                            animate={{ scale: currentSlide === 0 ? 1 : 0 }}
                            transition={{
                              delay: 0.2,
                              type: "spring",
                              stiffness: 200,
                            }}
                          >
                            <i className="bi bi-bullseye text-white fs-1"></i>
                          </motion.div>
                          <h2 className="section-title mb-4 text-center">
                            Our Mission
                          </h2>
                          <p className="lead text-muted mb-4">
                            At Pranayuv, we envision a world where healthcare is
                            smarter, more compassionate, and accessible to all.
                            We're on a mission to build innovative wellness
                            products that are both technologically advanced and
                            affordable, making them ideal for individuals,
                            caregivers, and medical institutions alike.
                          </p>
                          <div className="row justify-content-center mt-4">
                            <div className="col-md-8">
                              <div className="row g-3">
                                <div className="col-4">
                                  <div className="bg-light rounded-3 p-3">
                                    <i className="bi bi-heart text-danger fs-3"></i>
                                    <h6 className="mt-2 mb-0">Compassionate</h6>
                                  </div>
                                </div>
                                <div className="col-4">
                                  <div className="bg-light rounded-3 p-3">
                                    <i className="bi bi-cpu text-primary fs-3"></i>
                                    <h6 className="mt-2 mb-0">Smart</h6>
                                  </div>
                                </div>
                                <div className="col-4">
                                  <div className="bg-light rounded-3 p-3">
                                    <i className="bi bi-universal-access text-success fs-3"></i>
                                    <h6 className="mt-2 mb-0">Accessible</h6>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                      <motion.div
                        className={`carousel-item ${
                          currentSlide === 1 ? "active" : ""
                        }`}
                        initial={{ opacity: 0, x: 100 }}
                        animate={{
                          opacity: currentSlide === 1 ? 1 : 0,
                          x: currentSlide === 1 ? 0 : -100,
                        }}
                        transition={{ duration: 0.6 }}
                      >
                        <div className="text-center">
                          <motion.div
                            className="bg-gradient rounded-circle p-4 d-inline-flex mb-4"
                            initial={{ scale: 0 }}
                            animate={{ scale: currentSlide === 1 ? 1 : 0 }}
                            transition={{
                              delay: 0.2,
                              type: "spring",
                              stiffness: 200,
                            }}
                          >
                            <i className="bi bi-eye text-white fs-1"></i>
                          </motion.div>
                          <h2 className="section-title mb-4 text-center">
                            Our Vision
                          </h2>
                          <p className="lead text-muted mb-4">
                            To be the global leader in healthcare innovation,
                            transforming lives through intelligent medical
                            technology. We envision a future where every person
                            has access to personalized, efficient, and
                            compassionate healthcare solutions that enhance
                            quality of life and promote wellness.
                          </p>
                          <div className="row justify-content-center mt-4">
                            <div className="col-md-8">
                              <div className="row g-3">
                                <div className="col-4">
                                  <div className="bg-light rounded-3 p-3">
                                    <i className="bi bi-globe text-info fs-3"></i>
                                    <h6 className="mt-2 mb-0">Global</h6>
                                  </div>
                                </div>
                                <div className="col-4">
                                  <div className="bg-light rounded-3 p-3">
                                    <i className="bi bi-person-check text-warning fs-3"></i>
                                    <h6 className="mt-2 mb-0">Personal</h6>
                                  </div>
                                </div>
                                <div className="col-4">
                                  <div className="bg-light rounded-3 p-3">
                                    <i className="bi bi-shield-check text-success fs-3"></i>
                                    <h6 className="mt-2 mb-0">Reliable</h6>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                    <div className="d-flex justify-content-center gap-3 mt-4">
                      <motion.button
                        className="btn btn-gradient rounded-circle"
                        type="button"
                        onClick={handlePrevSlide}
                        style={{ width: "50px", height: "50px" }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </motion.button>
                      <motion.button
                        className="btn btn-gradient rounded-circle"
                        type="button"
                        onClick={handleNextSlide}
                        style={{ width: "50px", height: "50px" }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </motion.button>
                    </div>
                    <div className="carousel-indicators position-relative mt-3">
                      <motion.button
                        type="button"
                        onClick={() => setCurrentSlide(0)}
                        className={`${currentSlide === 0 ? "active" : ""}`}
                        aria-current={currentSlide === 0 ? "true" : "false"}
                        aria-label="Mission"
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                      ></motion.button>
                      <motion.button
                        type="button"
                        onClick={() => setCurrentSlide(1)}
                        className={`${currentSlide === 1 ? "active" : ""}`}
                        aria-current={currentSlide === 1 ? "true" : "false"}
                        aria-label="Vision"
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                      ></motion.button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Section */}
              <div className="mb-5">
                <div className="p-5">
                  <h2 className="section-title">Meet Our Visionaries</h2>
                  <p className="lead text-center text-muted mb-5">
                    The brilliant minds driving innovation and transforming
                    healthcare through technology
                  </p>
                  <div className="row g-4 justify-content-center">
                    {team.map((member, index) => (
                      <motion.div
                        key={member.name}
                        className="col-lg-4 col-md-6"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.8 }}
                        viewport={{ once: true }}
                      >
                        <div className="team-card1 h-100 border-0 shadow">
                          <div className="card1-body text-center p-4 position-relative">
                            <motion.img
                              src={member.img}
                              alt={member.name}
                              className="team-photo rounded-circle mb-3"
                              style={{
                                width: "120px",
                                height: "120px",
                                objectFit: "cover",
                              }}
                              whileHover={{ scale: 1.05 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            />
                            <h4 className="card1-title h5 fw-bold mb-2">
                              {member.name}
                            </h4>
                            <p className="text-gradient fw-semibold mb-2">
                              {member.role}
                            </p>
                            <p className="text-muted small mb-3">
                              {member.description}
                            </p>
                            <motion.a
                              href={member.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-connect"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <i className="bi bi-linkedin me-2"></i>
                              Connect
                            </motion.a>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Global Presence Section */}
              <div className="text-center mb-4"></div>

              <div className="globe-section py-4 mb-5">
                <div className="container-fluid p-4">
                  <h2 className="section-title">Our Global Footprint</h2>
                  <p className="lead text-muted mb-0">
                    <center>
                      {" "}
                      Strategically positioned to drive innovation across
                      India's healthcare landscape
                    </center>
                  </p>
                  <div className="row g-4 align-items-center">
                    {/* Vijayawada Hub - Left */}
                    <motion.div
                      className="col-lg-4"
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8 }}
                      viewport={{ once: true }}
                    >
                      <div className="location-text">
                        <div className="d-flex align-items-center mb-3">
                          <div className="bg-gradient rounded-circle p-2 me-3">
                            <i className="bi bi-geo-alt text-white"></i>
                          </div>
                          <h4 className="text-gradient fw-bold mb-0">
                            Vijayawada Hub
                          </h4>
                        </div>
                        <p className="mb-0">
                          Room No.220, EEE Dept., V.R.Siddhartha College,
                          <br />
                          Chalasani Nagar, Kanuru, Vijayawada,
                          <br />
                          Andhra Pradesh 520007, India
                        </p>
                      </div>
                    </motion.div>

                    {/* Globe - Middle */}
                    <motion.div
                      className="col-lg-4"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8 }}
                      viewport={{ once: true }}
                    >
                      <div className="globe-container">
                        <Globe
                          ref={globeRef}
                          globeImageUrl="//unpkg.com/three-globe/example/img/earth-day.jpg"
                          backgroundColor="rgba(0,0,0,0)"
                          pointsData={marker}
                          pointLat="lat"
                          pointLng="lng"
                          pointColor="color"
                          pointRadius={0.8}
                          pointLabel="label"
                          pointsMerge={true}
                          pointAltitude={0.02}
                          labelSize={1.2}
                          labelColor={() => "#ffffff"}
                          labelAltitude={0.02}
                          autoRotate={true}
                          autoRotateSpeed={0.8}
                          width={
                            window.innerWidth < 768
                              ? window.innerWidth - 64
                              : 400
                          }
                          height={
                            window.innerWidth < 768
                              ? window.innerWidth - 64
                              : 400
                          }
                        />
                      </div>
                    </motion.div>

                    {/* Research Center - Right */}
                    <motion.div
                      className="col-lg-4"
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8 }}
                      viewport={{ once: true }}
                    >
                      <div className="location-text">
                        <div className="d-flex align-items-center mb-3">
                          <div className="bg-gradient rounded-circle p-2 me-3">
                            <i className="bi bi-building text-white"></i>
                          </div>
                          <h4 className="text-gradient fw-bold mb-0">
                            Research Center
                          </h4>
                        </div>
                        <p className="mb-0">
                          AMTZ Campus, Pragati Maidan, VM Steel Project S.O,
                          <br />
                          Visakhapatnam – 530031, Andhra Pradesh, India
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {isVideoOpen && (
        <div
          className="video-modal"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setIsVideoOpen(false)}
        >
          <div
            className="video-modal-content"
            style={{
              position: "relative",
              width: "90%",
              maxWidth: "800px",
              backgroundColor: "#fff",
              borderRadius: "8px",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <span
              className="close-button"
              style={{
                position: "absolute",
                top: "10px",
                right: "15px",
                fontSize: "28px",
                fontWeight: "bold",
                color: "#000",
                cursor: "pointer",
                zIndex: 1001,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                borderRadius: "50%",
                width: "35px",
                height: "35px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => setIsVideoOpen(false)}
            >
              &times;
            </span>
            <iframe
              width="100%"
              height="450"
              src="https://www.youtube.com/embed/e9LKjmj4guk?si=BGBWntfIy3QaIu5u"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};
export default About;
